import React, {
  FC,
  useCallback,
  useMemo,
  useState,
}                              from 'react';
import moment, {
  MomentInput,
  Moment,
}                              from 'moment';
import { ISearchInvoice }      from '@typings/invoiceManagement';
import {
  ISearchFilter,
  ISearchParams,
  TSearchOperator,
}                              from '@typings/search';
import { TDataTableRow }       from '@typings/components';
import {
  EInvoiceStatus,
  EInvoicePage,
}                              from '@enums/invoiceManagement';
import { EAppPage }            from '@enums/common';
import { EAccountPage }        from '@enums/accountManagement';
import { useFetchInvoices }    from '@modules/invoiceManagement/queries/invoiceManagementQueries';
import { ROWS_PER_PAGE }       from '@utils/suggestions';
import {
  parseRecords,
  formatCurrency,
  formatParsedZoneDate,
}                              from '@utils/commonFunctions';
import { INVOICE_STATUS_NAME } from '@modules/invoiceManagement/utils/suggestions';
import { EstimateInvoicePage } from '../pages/EstimateInvoicePage';

const UNPAID_STATUS_FILTER: ISearchFilter = {
  propertyName : 'Status',
  operator     : 'eq',
  value        : EInvoiceStatus.Unpaid,
};

const resetDateToStartDay = (date: MomentInput): string => {
  if (!date) {
    return '';
  }

  const startDayDate: Moment = moment(date).startOf('day');

  const formattedDate : string = startDayDate.format();
  const offset        : string = startDayDate.format('Z');

  return formattedDate.replace(offset, 'Z');
};

const getInvoiceDateFilter = (
  operator : TSearchOperator,
  value    : string,
): ISearchFilter => ({
  propertyName: 'InvoiceDate',
  operator,
  value,
});

const createFilterByPeriod = (
  page         : EInvoicePage,
  historyDate? : string,
): ISearchFilter[] => {
  const today                  = moment().toISOString();
  const firstDayOfInvoiceMonth = moment().subtract(1, 'months').startOf('month');

  const startOfHistoryMonth = moment(historyDate).startOf('month');
  const endOfHistoryMonth   = moment(historyDate).endOf('month');

  const overThirtyDays = moment(firstDayOfInvoiceMonth).subtract(1, 'months').endOf('month');
  const overSixtyDays  = moment(firstDayOfInvoiceMonth).subtract(2, 'months').endOf('month');
  const overNinetyDays = moment(firstDayOfInvoiceMonth).subtract(3, 'months').endOf('month');

  switch (page) {
    case EInvoicePage.Current:
      return [
        getInvoiceDateFilter('ge', resetDateToStartDay(firstDayOfInvoiceMonth)),
        getInvoiceDateFilter('le', today),
      ];

    case EInvoicePage.OverThirtyDays:
      return [getInvoiceDateFilter('le', resetDateToStartDay(overThirtyDays))];

    case EInvoicePage.OverSixtyDays:
      return [getInvoiceDateFilter('le', resetDateToStartDay(overSixtyDays))];

    case EInvoicePage.OverNinetyDays:
      return [getInvoiceDateFilter('le', resetDateToStartDay(overNinetyDays))];

    case EInvoicePage.CurrentDue:
      return [
        UNPAID_STATUS_FILTER,
        getInvoiceDateFilter('ge', resetDateToStartDay(firstDayOfInvoiceMonth)),
        getInvoiceDateFilter('le', today),
      ];

    case EInvoicePage.PastDue:
      return [
        UNPAID_STATUS_FILTER,
        getInvoiceDateFilter('lt', resetDateToStartDay(firstDayOfInvoiceMonth)),
      ];

    case EInvoicePage.Monthly:
      return [
        getInvoiceDateFilter('ge', resetDateToStartDay(startOfHistoryMonth)),
        getInvoiceDateFilter('le', resetDateToStartDay(endOfHistoryMonth)),
      ];

    default:
      return [];
  }
};

const getInvoiceData = (
  searchedInvoices: ISearchInvoice[],
): TDataTableRow[] => searchedInvoices.map((invoice) => [{
  value : invoice.invoiceNumber,
  link  : `${EAppPage.AccountManagement}/${invoice.cuid}/${EAccountPage.Invoices}/${invoice.id}`,
}, {
  value : invoice.creditUnion.name,
  align : 'left',
}, {
  value: formatParsedZoneDate(invoice.invoiceDate),
}, {
  value : INVOICE_STATUS_NAME[invoice.status],
  align : 'center',
}, {
  value : formatCurrency(invoice.amount),
  align : 'center',
}, {
  value : formatParsedZoneDate(invoice.paidDate, 'LL', 'Unpaid'),
  align : 'center',
}]);

interface IProps {
  page         : EInvoicePage;
  historyDate? : string;
}

export const EstimateInvoiceContainer: FC<IProps> = ({ page, historyDate }) => {
  const filters = useMemo(
    () => createFilterByPeriod(page, historyDate),
    [page, historyDate],
  );

  const [currentPage, setCurrentPage] = useState<number>(0);
  const [payload, setPayload]         = useState<ISearchParams>({
    offset : 0,
    limit  : ROWS_PER_PAGE,
    filters,
  });

  // #region API INTEGRATION
  const {
    data: invoicesData,
    isLoading,
  } = useFetchInvoices(payload);
  // #endregion

  const rowInvoiceData = useMemo<TDataTableRow[]>(
    () => {
      if (!invoicesData) {
        return [];
      }

      const invoices: ISearchInvoice[] = parseRecords<ISearchInvoice>(invoicesData);
      return getInvoiceData(invoices);
    },
    [invoicesData],
  );

  const totalInvoicesCount = useMemo(
    () => invoicesData?.totalRecordsFound ?? 0,
    [invoicesData],
  );

  const handleChangePage = useCallback(
    (pageNumber: number): void => {
      setCurrentPage(pageNumber);
      setPayload((prev) => ({ ...prev, offset: pageNumber * ROWS_PER_PAGE }));
    },
    [],
  );

  return (
    <EstimateInvoicePage
      rowInvoiceData     = {rowInvoiceData}
      totalInvoicesCount = {totalInvoicesCount}
      isLoading          = {isLoading}
      currentPage        = {currentPage}
      rowsPerPage        = {ROWS_PER_PAGE}
      handleChangePage   = {handleChangePage}
    />
  );
};
